import type { ReactElement } from 'react';
import { Button as AriaButton } from 'react-aria-components';
import { useSignOut } from '../app/routes';
import { useSession } from '../app/session-context';
import { Icon } from '../ui/Icon';
import { Menu, MenuItem } from '../ui/Menu';

/** The signed-in person (initial and email or admin label), with Sign out. The rail shows the initial alone. */
export function AccountMenu({ compact = false }: { compact?: boolean }): ReactElement {
  const { snapshot } = useSession();
  const showAgents = snapshot.mode === 'accounts' && snapshot.config?.mcpOauth === true;
  // Signs out, shows sign-in, then empties the query cache (see useSignOut).
  const signOut = useSignOut();
  const label = snapshot.userLabel?.trim() || null;
  // No letter until the label is known: an empty circle, never a bare chevron.
  const initial = label ? (Array.from(label)[0] ?? '').toUpperCase() : '';

  const trigger = (
    <AriaButton
      aria-label="Account menu"
      className="flex items-center gap-2 min-w-0 max-w-full m-0 px-3 py-2 border-0 rounded-control bg-transparent text-on-nav-default font-ui text-md appearance-none cursor-pointer focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-on-nav-strong"
    >
      <span
        data-testid="account-initial"
        title={label ?? undefined}
        aria-hidden="true"
        className="flex shrink-0 items-center justify-center w-5 h-5 rounded-pill bg-on-nav-hover text-on-nav-strong text-xs font-semibold"
      >
        {initial}
      </span>
      {label && !compact ? <span title={label} className="min-w-0 grow truncate text-left">{label}</span> : null}
      {/* The 56px rail has no room for the chevron beside the initial; it clipped at the edge. */}
      {compact ? null : <Icon name="chevron" size={14} />}
    </AriaButton>
  );

  return (
    <Menu label="Account" trigger={trigger}>
      {showAgents ? (
        <MenuItem id="connected-agents" href="/account/agents">
          Connected agents
        </MenuItem>
      ) : null}
      <MenuItem id="sign-out" onAction={signOut}>
        Sign out
      </MenuItem>
    </Menu>
  );
}
